import {
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { TableColumnsType } from "antd";
import _ from "lodash";

import { ILongTermPlan } from "@types";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";
import { roles } from "@/constants";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const installment_types = [
  {
    value: "ANNUALLY",
    label: "Annually",
  },
  {
    value: "MONTHLY",
    label: "Monthly",
  },
  {
    value: "QUARTERLY",
    label: "Quarterly",
  },
  { value: "SEMI_ANNUALLY", label: "Semi Annually" },
];

export const grant_types = [
  {
    value: "ADHOC",
    label: "Adhoc",
  },
  {
    value: "ANNUAL",
    label: "Annual",
  },
];

export const vesting_types = [
  {
    value: "CLIFF",
    label: "Cliff",
  },
  {
    value: "INSTALLMENT",
    label: "Installment",
  },
];

export const grant_based_types = [
  {
    value: "UNITS",
    label: "Units",
  },
  {
    value: "VALUE",
    label: "Value",
  },
];

export const holding_periods = [
  {
    value: 1,
    label: 1,
  },
  {
    value: 2,
    label: 2,
  },
  {
    value: 3,
    label: 3,
  },
  {
    value: 4,
    label: 4,
  },
  {
    value: 5,
    label: 5,
  },
];

export const options = [
  { id: "holding_period", label: "Period" },
  { id: "is_installment", label: "Installment" },
  { id: "is_amount", label: "Amount" },
  { id: "is_adhoc", label: "Adhoc" },
];

export const getColumns = ({
  removeLongTermPlan,
  longTermPlan_id,
  isDeleting,
  editLongTermPlan,
  userRole,
}: {
  removeLongTermPlan: (id: number) => Promise<void>;
  longTermPlan_id: React.MutableRefObject<any>;
  isDeleting: boolean;
  editLongTermPlan: (longTermPlan: ILongTermPlan) => void;
  userRole: string;
}): TableColumnsType<any> => [
    {
      title: "plan name",
      dataIndex: "name",
      key: "name",
      width: 200,
      sorter: true,
    },
    {
      title: "vesting type",
      dataIndex: "is_installment",
      key: "is_installment",
      width: 200,
      render: (is_installment: boolean) =>
        is_installment ? "Installment" : "Cliff",
      sorter: true,
    },
    {
      title: "holding period",
      dataIndex: "holding_period",
      key: "holding_period",
      width: 200,
      render: value => {
        return value <= 0 ? "" : value;
      },
      sorter: true,
    },
    {
      title: "installment schedule",
      dataIndex: "installment_type",
      key: "installment_type",
      width: 200,
      render: (type: string) => _.replace(_.startCase(_.toLower(type)), "_", " "),
      sorter: true,
    },
    {
      title: "type of stock",
      dataIndex: "type",
      key: "type",
      width: 300,
      render: (type: { id: number; name: string }) => (
        <div className="text-wrap">{type?.name}</div>
      ),
      sorter: true,
    },
    {
      title: "currency",
      key: "currency",
      width: 200,
      render: (plan: ILongTermPlan) => {
        return plan?.currency?.name || "";
      },
      sorter: true,
    },



    {
      title: "grades",
      key: "grades",
      width: 250,
      render: (plan: ILongTermPlan) => {
        return (
          <div className="text-wrap">
            {plan?.is_all_grade
              ? "All Grades"
              : plan?.grades
                ?.map((grade: any) => grade?.grade)
                .join(", ")}
          </div>
        );
      },
      sorter: true,
    },
    {
      title: "grade type",
      key: "type",
      width: 250,
      render: (plan: ILongTermPlan) => {
        return (
          <div className="text-wrap">
            {plan?.is_all_grade
              ? "All Types"
              : plan?.grades
                ?.map((grade: any) => grade?.type)
                .join(", ")}
          </div>
        );
      },
      sorter: true,
    },


    {
      title: "Countries",
      key: "country",
      width: !checkPermission([
        permissions.DELETE_COMPANY_LTI,
        permissions.UPDATE_COMPANY_LTI,
      ])
        ? 200
        : 200,
      render: (plan: ILongTermPlan) => (
        <div className="text-wrap">
          {plan?.is_global
            ? "Global"
            : plan?.countries?.map(country => country?.name).join(", ")}
        </div>
      ),
      sorter: true,
    },


    {
      title: "grant type",
      key: "is_adhoc",
      width: 200,
      render: (plan: any) => {
        return plan?.is_adhoc ? "Adhoc" : "Annual";
      },
      sorter: true,
    },
    {
      title: "date of grant",
      key: "date",
      width: 200,
      render: (plan: ILongTermPlan) => {
        const date = plan?.date_of_grant ?? "";
        const month =
          plan?.month_of_grant && months[plan.month_of_grant - 1]
            ? months[plan.month_of_grant - 1]
            : "";
        return `${date} ${month}`.trim();
      },
      sorter: true,
    },
    {
      title: "grant based type",
      dataIndex: "is_amount",
      key: "is_amount",
      width: 200,
      render: (is_amount: boolean) => (is_amount ? "Amount" : "Unit"),
      sorter: true,
    },

    ...(!checkPermission([
      permissions.DELETE_COMPANY_LTI,
      permissions.UPDATE_COMPANY_LTI,
    ])
      ? []
      : ([
        {
          title: "Actions",
          key: "actions",
          width: 160,
          fixed: "right",
          align: "center",
          render: (longTermPlan: ILongTermPlan) => (
            <div className="flex items-center gap-2">
              {checkPermission(permissions.UPDATE_COMPANY_LTI) && (
                <div
                  onClick={event => {
                    event.stopPropagation();
                    editLongTermPlan(longTermPlan);
                  }}
                  className="table__action__btn table__action__btn--client"
                >
                  <EditOutlined style={{ color: "#1A8245" }} /> Edit
                </div>
              )}
              {checkPermission(permissions.DELETE_COMPANY_LTI) && (
                <div
                  onClick={event => {
                    event.stopPropagation();
                    removeLongTermPlan(longTermPlan?.id);
                  }}
                  className="table__action__btn table__action__btn--delete"
                >
                  {isDeleting &&
                    longTermPlan?.id === longTermPlan_id?.current ? (
                    <LoadingOutlined color="#B62424" className="spinner" />
                  ) : (
                    <>
                      <DeleteOutlined style={{ color: "#B62424" }} /> Delete
                    </>
                  )}
                </div>
              )}
            </div>
          ),
        },
      ] as any)),
  ];
